import { getOngoingSyncs, getOrderRelistingsCount, getUnprocessedTransactions } from 'lib/api'
import { useRelistingStore } from 'lib/hooks/useRelistingStore'
import { useTransactionsSpinnerStore } from 'lib/hooks/useTransactionsSpinnerStore'
import { useCallback, useState } from 'react'

export interface TransactionInfo {
  type: string
  quantityUnprocessed: number
  quantityFailed: number
}

export interface SyncInfo {
  type: string
  ongoingSyncs: string[]
}

// A fetch that is over and waiting on the user: apart from the ongoing syncs,
// so the spinner does not present it as work in progress.
export interface AwaitingUserInfo {
  type: string
}

export const useTransactionsPolling = () => {
  const { setShowSpinnerLoading, setShowSidebarWarning, setNumberOfFailedTransactions } =
    useTransactionsSpinnerStore()
  const { setShowRelistingsBanner } = useRelistingStore()

  const [transactions, setTransactions] = useState<TransactionInfo[]>([])
  const [syncs, setSyncs] = useState<SyncInfo[]>([])
  const [awaitingUser, setAwaitingUser] = useState<AwaitingUserInfo[]>([])

  const fetchUnprocessedTransactions = useCallback(async () => {
    try {
      const orderRelistingsCount = await getOrderRelistingsCount()
      setShowRelistingsBanner(orderRelistingsCount.count > 0)

      const transactionsResponse = await getUnprocessedTransactions()
      const transactionsData: TransactionInfo[] = transactionsResponse.data.data.map(
        (item: any) => ({
          type: item.type,
          quantityUnprocessed: item.quantity_unprocessed,
          quantityFailed: item.quantity_failed,
        })
      )

      const syncResponse = await getOngoingSyncs()
      const syncData: SyncInfo[] = syncResponse.data.map((item: any) => ({
        type: item.type,
        ongoingSyncs: item.ongoing_syncs,
      }))
      const awaitingUserData: AwaitingUserInfo[] = (syncResponse.awaiting_user ?? []).map(
        (item: any) => ({ type: item.type })
      )

      setTransactions(transactionsData)
      setSyncs(syncData)
      setAwaitingUser(awaitingUserData)

      const hasUnprocessedOrFailedTransactions = transactionsData.some(
        (t) => t.quantityUnprocessed > 0 || t.quantityFailed > 0
      )
      const hasUnprocessedSyncs = syncData.some((s) => s.ongoingSyncs.length > 0)

      // Waiting on the user keeps the spinner alive, or the poll stops on the
      // very transition we want to tell them about.
      setShowSpinnerLoading(
        hasUnprocessedOrFailedTransactions || hasUnprocessedSyncs || awaitingUserData.length > 0
      )

      if (transactionsData.length > 0 && transactionsData[0].quantityFailed) {
        setNumberOfFailedTransactions(transactionsData[0].quantityFailed)
        setShowSidebarWarning(true)
      }
    } catch (error) {
      console.log('Error:', error)
      setShowSpinnerLoading(false)
    }
  }, [])

  return { fetchUnprocessedTransactions, transactions, syncs, awaitingUser }
}
